<template>
  <div class="virtual-table-container">
    <!-- 统一的滚动容器 -->
    <div class="scroll-container" ref="scrollContainer" @scroll="handleScroll">
      <!-- 表格头部 -->
      <div class="table-header" :style="{ width: tableWidth + 'px' }">
        <div class="header-row">
          <div class="header-cell index-column fixed-column fixed-first" :style="{ width: '80px' }">
            <span class="required-header">
              排序
            </span>
          </div>
          <!-- 序号列 -->
          <div class="header-cell index-column fixed-column fixed-second" :style="{ width: '70px' }">
            <span class="required-header">
              序号</span>
          </div>
          <!-- 起始地址列 -->
          <div class="header-cell index-column fixed-column fixed-third" :style="{ width: '170px' }">
            <span class="required-star">* </span>
            <span class="required-header">起始地址（0x）</span>
          </div>
          <!-- 子索引列 -->
          <div class="header-cell index-column fixed-column fixed-four" :style="{ width: '100px' }" v-if="protocolType === '1'">
            <span class="required-star">* </span>
            <span class="required-header">子索引</span>
          </div>
          <!-- 动态列 -->
          <div v-for="(item, index) in protocolTableList" :key="index" 
               class="header-cell" 
               :style="{ width: getColumnWidth(item) + 'px' }">
            <span class="required-header">
              <span class="required-star" v-if="item.required">*</span>
              {{ item.label }}
            </span>
          </div>
          <!-- 操作列 -->
          <div class="header-cell" :style="{ width: '120px' }">
            操作
          </div>
        </div>
      </div>
      
      <!-- 虚拟滚动容器 -->
      <div class="virtual-scroll-container" :style="{ maxHeight: totalHeight > 550 ? '550px' : 'auto' }">
        
        <!-- 总高度容器 -->
        <div class="total-height" :style="{ height: totalHeight + 'px', width: tableWidth + 'px' }">
          
          <!-- 可视区域 -->
          <div class="visible-rows" 
               :style="{ transform: `translateY(${offsetY}px)`, width: tableWidth + 'px' }">
            
            <!-- 渲染的行 -->
            <div v-for="(row, rowIndex) in visibleRows" 
                :key="getActualRowIndex(rowIndex)"
                class="table-row"
                :class="{ 'warning-row': isDuplicateRow(row), 'dragging': draggedIndex === getActualRowIndex(rowIndex), 'drag-over': draggedOverIndex === getActualRowIndex(rowIndex) }"
                @dragover="handleDragOver($event, rowIndex)"
                @drop="handleDrop($event, rowIndex)">
              <!-- 排序列 -->
              <div class="table-cell index-column fixed-column fixed-first" :style="{ width: '80px' }">
                <i class="el-icon-rank drag-handle" 
                  draggable="true"
                  @dragstart="handleDragStart($event, rowIndex)"
                  @dragend="handleDragEnd"></i>
              </div>
              <!-- 序号列 -->
              <div class="table-cell index-column fixed-column fixed-second" :style="{ width: '70px' }">
                {{ getActualRowIndex(rowIndex) + 1 }}
              </div>
              
              <!-- 起始地址列 -->
              <div class="table-cell index-column fixed-column fixed-third" :style="{ width: '170px' }">
                <DynamicCell 
                  ref="dynamicCell"
                  :item="{ prop: 'startAddress', label: '起始地址（0x）', type: 'input', required: true }" 
                  :data="row" 
                  :cellRules="protocolTableRules"
                  :index="getActualRowIndex(rowIndex)"
                  :column="0"
                  @remote="emitChangeGroup($event, getActualRowIndex(rowIndex))" 
                  :allMappingData="allMappingData" 
                  @selected="selected" 
                  @emitChangeGroup="emitChangeGroup" 
                  @switch="switchChange" />
              </div>
              
              <!-- 子索引列 -->
              <div class="table-cell index-column fixed-column fixed-four" :style="{ width: '100px' }" v-if="protocolType === '1'">
                <DynamicCell 
                  ref="dynamicCell"
                  :item="{ prop: 'subIndex', label: '子索引', type: 'input', required: true }" 
                  :data="row" 
                  :cellRules="protocolTableRules"
                  :index="getActualRowIndex(rowIndex)"
                  :column="1"
                  @remote="emitChangeGroup($event, getActualRowIndex(rowIndex))" 
                  :allMappingData="allMappingData" 
                  @selected="selected" 
                  @emitChangeGroup="emitChangeGroup" 
                  @switch="switchChange" />
              </div>
              
              <!-- 动态列 -->
              <div v-for="(item, colIndex) in protocolTableList" 
                   :key="colIndex"
                   class="table-cell"
                   :style="{ width: getColumnWidth(item) + 'px' }">
                <DynamicCell 
                  ref="dynamicCell"
                  :item="item" 
                  :data="row" 
                  :cellRules="protocolTableRules"
                  :index="getActualRowIndex(rowIndex)"
                  :column="colIndex + (protocolType === '1' ? 2 : 1)"
                  @remote="emitChangeGroup($event, getActualRowIndex(rowIndex))" 
                  :allMappingData="allMappingData" 
                  @selected="selected" 
                  @emitChangeGroup="emitChangeGroup" 
                  @switch="switchChange" />
              </div>
              
              <!-- 操作列 -->
              <div class="table-cell" :style="{ width: '120px' }">
                <TableOperationBar 
                  @addRow="addRow(getActualRowIndex(rowIndex))" 
                  @removeRow="removeRow(getActualRowIndex(rowIndex))" 
                  @copyRow="copyRow({ row, $index: getActualRowIndex(rowIndex) })" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { protocolTableList } from '@/data/tableList'
import { protocolTableRules } from '@/data/rules'
import { handleDragRow, handleAddRow, handleDeleteRow, hasDuplicateData, hasDuplicateCombined, handleCopyRow } from '@/utils/tableRowHandlers'
import TableOperationBar from '@/components/table/TableOperationBar.vue'
import DynamicCell from '@/components/table/DynamicCell.vue'
import { getMappingformName } from '@/api/mappingform/mappingform'

export default {
  name: 'ProtocolTable',
  components: {
    TableOperationBar,
    DynamicCell
  },
  props: {
    // 表格数据
    tableData: {
      type: Array,
      default: () => []
    },
    // 表格类型 add: 创建 edit: 编辑
    type: {
      type: String,
      default: 'add'
    },
    // 是否有子索引（CAN协议才有）
    isSubIndex: {
      type: Boolean,
      default: false
    },
    protocolType: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      // 协议表头列表
      protocolTableList: JSON.parse(JSON.stringify(protocolTableList)), // 深拷贝避免修改原配置
      protocolTableRules: protocolTableRules,
      // 数据格式
      dataFormat: {
        index: 0,
        subIndex: '',
        startAddress: '0000',
        dataName: '',
        dataLength: '',
        dataType: '',
        needMapping: false,
        mappingType: '',
        mappingId: '',
        mappingName: '',
        unit: '',
        decimal: '',
        rangeMin: '',
        rangeMax: '',
        remark: ''
      },
      // 重复地址
      duplicateAddresses: [],
      allMappingData: [],
      duplicateCombined: [], // 存储重复的 "startAddress-subIndex" 拼接值
      
      // 虚拟滚动相关
      rowHeight: 50, // 每行高度
      visibleCount: 16, // 可视区域行数
      startIndex: 0, // 开始索引
      offsetY: 0, // 偏移量
      tableWidth: 0, // 表格宽度
      scrollTimer: null, // 滚动定时器
      
      // 拖拽相关
      draggedIndex: null,
      draggedOverIndex: null
    }
  },
  computed: {
    // 本地协议表格数据
    protocolTableData: {
      get () {
        return this.tableData
      },
      set (val) {
        this.$emit('update:tableData', val)
      }
    },
    // 总高度
    totalHeight () {
      return this.protocolTableData.length * this.rowHeight
    },
    // 可视区域的行
    visibleRows () {
      const endIndex = Math.min(this.startIndex + this.visibleCount, this.protocolTableData.length)
      return this.protocolTableData.slice(this.startIndex, endIndex)
    },
    selectDictionary () {
      return this.$store.getters.getDictionaryList
    }
  },
  mounted () {
    // 计算表格宽度
    this.calculateTableWidth()
    
    // 编新建协议时，初始化表格数据
    if (this.type === 'add' && this.protocolTableData.length === 0) {
      this.protocolTableData.push(JSON.parse(JSON.stringify(this.dataFormat)))
    }
    
    this.$assignDictOptions(this.protocolTableList, { dataType: 'data_type' }, this.selectDictionary)
    this.$assignDictOptions(this.protocolTableList, { mappingType: 'mapping_type' }, this.selectDictionary)
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.calculateTableWidth)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calculateTableWidth)
    // 清理滚动定时器
    if (this.scrollTimer) {
      cancelAnimationFrame(this.scrollTimer)
    }
  },
  watch: {
    // 监听协议表格数据变化
    protocolTableData: {
      handler (val) {
        if (!this.isSubIndex) {
          // 检查起始地址是否有重复
          this.duplicateAddresses = hasDuplicateData(this.protocolTableData, 'startAddress')
          if (this.duplicateAddresses.length > 0) {
            this.$message.warning('存在重复的起始地址，请检查')
          }
        } else {
          // 检查 startAddress + subIndex 拼接后是否重复
          this.duplicateCombined = hasDuplicateCombined(this.protocolTableData, 'startAddress', 'subIndex')
          if (this.duplicateCombined.length > 0) {
            this.$message.warning('CAN协议的起始地址+子索引不能重复，请检查')
          }
        }
        // 向父组件传递tableData变化
        this.$emit('valueChange', val)
      },
      deep: true
    },
    // 监听isSubIndex变化
    isSubIndex: {
      handler(newVal) {
        // 查找是否已存在subIndex列
        const subIndexExists = this.protocolTableList.some(item => item.prop === 'subIndex')
        
        if (newVal && !subIndexExists) {
          // 当isSubIndex为true且不存在subIndex列时，插入子索引列（插入到序号列后面）
          // this.protocolTableList.splice(1, 0, {
          //   label: '子索引',
          //   prop: 'subIndex',
          //   type: 'input',
          //   required: true,
          //   width: 80
          // })
        } else if (!newVal && subIndexExists) {
          // 当isSubIndex为false且存在subIndex列时，移除子索引列
          const subIndexIndex = this.protocolTableList.findIndex(item => item.prop === 'subIndex')
          this.protocolTableList.splice(subIndexIndex, 1)
        }
        
        // 重新计算表格宽度
        this.$nextTick(() => {
          this.calculateTableWidth()
        })
      },
      // 初始化时立即执行一次
      immediate: true
    },
    // 监听protocolType变化，重新计算表格宽度
    protocolType: {
      handler() {
        this.$nextTick(() => {
          this.calculateTableWidth()
        })
      },
      immediate: true
    }
  },
  methods: {
    // 获取列宽度 - 严格按照tableList配置
    getColumnWidth (item) {
      return item.width || 150 // 使用配置的宽度，如果没有配置则使用默认宽度150px
    },
    
    // 计算表格宽度 - 根据protocolType动态计算
    calculateTableWidth () {
      let width = 80 // 序号列宽度
      width += 240 // 起始地址列宽度
      
      // 根据protocolType决定是否添加子索引列宽度
      if (this.protocolType === '1') {
        width += 100 // 子索引列宽度
      }
      
      // 计算动态列宽度 - 严格按照配置
      this.protocolTableList.forEach(item => {
        width += this.getColumnWidth(item)
      })
      
      width += 120 // 操作列宽度
      this.tableWidth = width
    },
    
    // 处理滚动事件 - 统一处理表头和表身的滚动
    handleScroll () {
      // 使用requestAnimationFrame优化滚动性能
      if (this.scrollTimer) {
        cancelAnimationFrame(this.scrollTimer)
      }
      
      this.scrollTimer = requestAnimationFrame(() => {
        const scrollContainer = this.$refs.scrollContainer
        if (!scrollContainer) return
        
        const scrollTop = scrollContainer.scrollTop
        
        // 更新垂直滚动相关数据
        const maxStartIndex = Math.max(0, this.protocolTableData.length - this.visibleCount)
        const newStartIndex = Math.min(Math.floor(scrollTop / this.rowHeight), maxStartIndex)
        const newOffsetY = newStartIndex * this.rowHeight
        
        // 确保startIndex和offsetY同步更新
        if (newStartIndex !== this.startIndex || newOffsetY !== this.offsetY) {
          this.startIndex = newStartIndex
          this.offsetY = newOffsetY
        }
      })
    },
    
    // 获取实际行索引
    getActualRowIndex (visibleIndex) {
      return this.startIndex + visibleIndex
    },
    
    // 判断是否为重复行
    isDuplicateRow (row) {
      if (!this.duplicateCombined) return false
      
      if (this.isSubIndex) {
        // 处理拼接值的情况
        const combinedValue = `${row.startAddress || ''}-${row.subIndex || ''}`
        // 检查当前行的拼接值是否在重复列表中
        const isDuplicate = this.duplicateCombined.some(dup => dup === combinedValue)
        return isDuplicate
      } else {
        // 处理仅检查 startAddress 的情况
        return this.duplicateAddresses.includes(row.startAddress)
      }
    },
    
    // 拖拽开始
    handleDragStart (event, index) {
      this.draggedIndex = this.getActualRowIndex(index)
      event.dataTransfer.effectAllowed = 'move'
    },
    
    // 拖拽经过
    handleDragOver (event, index) {
      event.preventDefault()
      this.draggedOverIndex = this.getActualRowIndex(index)
    },
    
    // 拖拽放下
    handleDrop (event, index) {
      event.preventDefault()
      const dropIndex = this.getActualRowIndex(index)
      
      if (this.draggedIndex !== null && this.draggedIndex !== dropIndex) {
        // 重新排序数据
        const draggedItem = this.protocolTableData[this.draggedIndex]
        this.protocolTableData.splice(this.draggedIndex, 1)
        this.protocolTableData.splice(dropIndex, 0, draggedItem)
        
        // 拖拽完成后重置虚拟滚动状态
        this.$nextTick(() => {
          const scrollContainer = this.$refs.scrollContainer
          if (scrollContainer) {
            const scrollTop = scrollContainer.scrollTop
            // 确保startIndex在有效范围内
            const maxStartIndex = Math.max(0, this.protocolTableData.length - this.visibleCount)
            const newStartIndex = Math.min(Math.floor(scrollTop / this.rowHeight), maxStartIndex)
            
            // 同步更新startIndex和offsetY
            this.startIndex = newStartIndex
            this.offsetY = newStartIndex * this.rowHeight
            
            // 强制重新渲染可见区域
            this.$forceUpdate()
          }
        })
      }
      
      this.draggedIndex = null
      this.draggedOverIndex = null
    },
    
    // 拖拽结束
    handleDragEnd () {
      this.draggedIndex = null
      this.draggedOverIndex = null
    },
    // 远程搜索-获取映射表名称列表
    async getMappingOptions (query, rowIndex) {
      let mappingType = ''
      if (this.protocolTableData[rowIndex].mappingType.value) {
        mappingType = this.protocolTableData[rowIndex].mappingType.value
      } else {
        mappingType = this.protocolTableData[rowIndex].mappingType
      }
      const data = {
        mappingName: query,
        mappingType: mappingType
      }
      
      const index = this.protocolTableList.findIndex(item => item.prop === 'mappingName')
      this.protocolTableList[index].loading = true
      const res = await getMappingformName(data)
      if (res.code === 200) {
        this.protocolTableList[index].options[0].options = res.data.valueMapping
        this.protocolTableList[index].options[0].label = res.data.valueMapping.length === 0 ? '' : '值映射'
        this.protocolTableList[index].options[1].options = res.data.bitMapping
        this.protocolTableList[index].options[1].label = res.data.bitMapping.length === 0 ? '' : 'bit位映射'
        this.protocolTableList[index].loading = false
        this.allMappingData = res.data.valueMapping.concat(res.data.bitMapping)
      }
    },
    
    selected ({ selectedOption, index, valueItem, column }) {
      this.getMappingOptions('', index)
      if (column === 3) return
      if (selectedOption) {
        this.protocolTableData[index].mappingId = selectedOption.value
        this.protocolTableData[index].mappingName = selectedOption.label
      } else {
        this.protocolTableData[index].mappingName = ''
        this.protocolTableData[index].mappingId = ''
      }
    },
    
    emitChangeGroup ({ selectedOption, index }) {
      if (selectedOption) {
        this.protocolTableData[index].mappingId = selectedOption.value
        this.protocolTableData[index].mappingName = selectedOption.label
      }
      this.getMappingOptions('', index)
    },
    
    switchChange ({ data, index, value }) {
      if (!value) {
        this.protocolTableData[index].mappingType = ''
        this.protocolTableData[index].mappingName = ''
        this.protocolTableData[index].mappingId = ''
      }
    },
    
    // 新增一行
    addRow (index) {
      if (this.isSubIndex) {
        this.dataFormat.startAddress = this.getNewAddress(this.protocolTableData[index].startAddress, 1)
      } else  {
        if (!this.protocolTableData[index].dataLength) {
          this.$message.warning('请先填写寄存器个数')
          return
        }
        this.dataFormat.startAddress = this.getNewAddress(this.protocolTableData[index].startAddress, this.protocolTableData[index].dataLength)
      }
      this.protocolTableData = handleAddRow(this.protocolTableData, this.dataFormat, index)
    },
    
    // 复制一行
    copyRow (copyRow) {
      const newCopyRow = JSON.parse(JSON.stringify(copyRow.row))
      newCopyRow.startAddress = '000' + (this.protocolTableData.length + 1)
      newCopyRow.id = ''
      if (newCopyRow.needMapping) {
        newCopyRow.mappingName = ''
        newCopyRow.mappingId = ''
      }
      this.protocolTableData = handleCopyRow(this.protocolTableData, newCopyRow, copyRow.$index)
    },
    
    // 删除一行
    removeRow (index) {
      if (this.protocolTableData.length <= 1) {
        this.$message.warning('至少保留一行')
      } else {
        this.protocolTableData = handleDeleteRow(this.protocolTableData, index)
      }
    },
    
    // 映射值校验
    handleValidation () {
      return Promise.all(this.$refs.dynamicCell.map(cell => cell.handleValidation()))
    },
    
    // 生成新的起始地址getNewAddress
    getNewAddress (startAddress, dataLength) {
      const address = parseInt(startAddress, 16) + parseInt(dataLength, 10)
      return address.toString(16).toUpperCase().padStart(4, '0')
    }
  }
}
</script>

<style scoped>
.virtual-table-container {
  width: 100%;
  border-radius: 10px;
  border: 1px solid #ebeef5;
  position: relative;
  overflow: hidden; /* 外层容器隐藏滚动条 */
}

/* 统一的滚动容器 */
.scroll-container {
  width: 100%;
  height: auto; /* 改为自适应高度 */
  min-height: 50px; /* 最小高度为表头高度 */
  max-height: calc(550px + 50px); /* 最大高度为表头+表体高度 */
  overflow-x: auto; /* 横向滚动 */
  overflow-y: auto; /* 垂直滚动 */
  position: relative;
}

.table-header {
  background-color: #fafafa;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid #ebeef5;
  min-width: 100%; /* 确保宽度足够 */
}

.header-row {
  display: flex;
  position: relative;
  min-width: 100%; /* 确保行宽度足够 */
}

.header-cell {
  padding: 8px 16px; /* 统一内边距 */
  text-align: center;
  color: #333;
  font-weight: bold;
  border-right: 1px solid #ebeef5;
  box-sizing: border-box;
  flex-shrink: 0; /* 防止收缩 */
  position: relative;
  min-width: 0; /* 防止内容溢出 */
  overflow: hidden; /* 防止内容溢出 */
  text-overflow: ellipsis; /* 文本溢出显示省略号 */
  white-space: nowrap; /* 不换行 */
}

.header-cell {
  font-size: 14px;
}

.header-cell:last-child {
  border-right: none;
}

.virtual-scroll-container {
  position: relative;
  border-top: none;
  overflow: visible; /* 改为visible，允许滚动 */
  width: 100%;
  max-height: 550px; /* 设置最大高度 */
  height: auto; /* 高度自适应 */
}

.total-height {
  position: relative;
  width: 100%; /* 确保宽度足够 */
}

.visible-rows {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%; /* 确保宽度足够 */
}

.table-row {
  display: flex;
  height: 50px;
  border-bottom: 1px solid #ebeef5;
  align-items: center;
  position: relative;
  width: 100%; /* 确保行宽度足够 */
  transition: all 0.3s ease; /* 添加过渡效果 */
}

.table-row:hover {
  background-color: #f5f7fa;
}

.table-cell {
  padding: 8px 16px; /* 与表头保持一致 */
  text-align: center;
  border-right: 1px solid #ebeef5;
  box-sizing: border-box;
  flex-shrink: 0; /* 防止收缩 */
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-width: 0; /* 防止内容溢出 */
  overflow: hidden; /* 防止内容溢出 */
}

.table-cell:last-child {
  border-right: none;
}

/* 固定列样式 */
.fixed-column {
  position: sticky;
  left: 0;
  z-index: 5;
  background-color: #f2fbff;
}

/* 第一列（排序列）固定 */
.fixed-first {
  left: 0;
  z-index: 15; /* 最高层级，确保在最前面 */
}

/* 第二列（序号列）固定 */
.fixed-second {
  left: 80px; /* 排序列的宽度 */
  z-index: 14; /* 第二高层级 */
}

/* 第三列（起始地址列）固定 */
.fixed-third {
  left: 150px; /* 排序列70px + 序号列70px */
  z-index: 13; /* 第三高层级 */
}
.fixed-four {
  left: 320px; /* 排序列70px + 序号列70px + 起始地址列170px */
  z-index: 12; /* 第四高层级 */
}
/* 序号列样式 */
.index-column {
  background-color: #fafafa;
}

/* 警告行样式 */
.warning-row {
  background-color: #fff3cd !important;
}

.warning-row:hover {
  background-color: rgba(255, 243, 205, 0.8) !important;
}

.required-star {
  color: #ff4d4f;
  margin-left: 2px;
}

/* 拖拽样式 */
.table-row[draggable="true"] {
  cursor: move;
}

.table-row.dragging {
  opacity: 0.5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scale(1.01);
  z-index: 100;
  background-color: transparent !important; /* 移除行级背景色 */
}

/* 拖拽时非固定列的背景色 - 使用更高优先级 */
.table-row.dragging > .table-cell:not(.fixed-column) {
  background-color: #e6f7ff !important;
}

/* 拖拽时固定列保持原有背景色 - 使用更高优先级和更具体的选择器 */
.table-row.dragging > .table-cell.fixed-column,
.table-row.dragging > .table-cell.fixed-first,
.table-row.dragging > .table-cell.fixed-second,
.table-row.dragging > .table-cell.fixed-third,
.table-row.dragging > .table-cell.fixed-four,
.table-row.dragging > .table-cell.index-column {
  background-color: #e6f7ff !important;
}

/* 确保拖拽时固定列的背景色不被任何其他样式覆盖 */
.table-row.dragging .fixed-column,
.table-row.dragging .fixed-first,
.table-row.dragging .fixed-second,
.table-row.dragging .fixed-third,
.table-row.dragging .fixed-four,
.table-row.dragging .index-column {
  background-color: #daf8f7 !important;
}

.table-row.drag-over {
  border: 1px solid #00d8c3;
  /* border-bottom: 3px solid #00d8c3; */
  background-color: #d6f8f5;
}

/* 拖拽手柄样式 */
.drag-handle {
  cursor: move;
  padding: 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.drag-handle:hover {
  background-color: #e6f7ff;
  color: #00d8c3;
}

/* 拖拽时行样式 */
.table-row.dragging {
  opacity: 0.8;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scale(1.01);
  z-index: 100;
}

.table-row.drag-over {
  border: 1px solid #00d8c3;
  background-color: #d6f8f5;
}

/* 拖拽位置指示器 */
.table-row.drag-over::before {
  content: '';
  position: absolute;
  top: -3px;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #b5f7f0, #b5f7f0);
  animation: dragIndicator 1s ease-in-out infinite;
}

@keyframes dragIndicator {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}
</style>